/**
 * SaveIndicator — transient "Saved Xs ago" pill that surfaces after a
 * successful save and auto-fades after a few seconds. Pure prop-driven so
 * any caller can wire it to whatever store slice tracks the save timestamp.
 */
import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

interface Props {
  /** Epoch ms of the last successful save. null/undefined → indicator hidden. */
  lastSaved: number | null | undefined;
  /** How long the indicator stays visible after each save event. Default 5000ms. */
  fadeAfterMs?: number;
  className?: string;
}

const DEFAULT_FADE_MS = 5000;

/** Format ms-since-saved as a short relative string. */
export function relativeTime(savedAt: number, now: number = Date.now()): string {
  const sec = Math.max(0, Math.round((now - savedAt) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

export function SaveIndicator({ lastSaved, fadeAfterMs = DEFAULT_FADE_MS, className }: Props) {
  const [visible, setVisible] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (lastSaved == null) { setVisible(false); return; }
    setVisible(true);
    setNow(Date.now());
    const fade = setTimeout(() => setVisible(false), fadeAfterMs);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearTimeout(fade); clearInterval(tick); };
  }, [lastSaved, fadeAfterMs]);

  if (!visible || lastSaved == null) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-text-secondary transition-opacity duration-500 ${className ?? ''}`}
      role="status"
      aria-live="polite"
    >
      <Check className="w-2.5 h-2.5 text-success" />
      Saved {relativeTime(lastSaved, now)}
    </span>
  );
}
